import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const OPS = [
  'equals', 'not_equals', 'contains', 'not_contains', 'is_empty', 'is_not_empty',
  'in', 'not_in', 'greater_than', 'less_than', 'boolean_true', 'boolean_false',
];
const ACTIONS = [
  'show', 'hide', 'require', 'optional', 'enable', 'disable', 'read_only',
  'set_default', 'clear_value', 'filter_options',
];

const emptyDraft = () => ({
  name: '',
  ruleKey: '',
  fieldKey: '',
  status: 'ACTIVE',
  logic: 'AND',
  clauseField: '',
  clauseOp: 'equals',
  clauseValue: '',
  actionType: 'show',
  actionField: '',
  actionValue: '',
});

const ConfigValidationRules = () => {
  const [pages, setPages] = useState([]);
  const [pageKey, setPageKey] = useState('qaqc');
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState(null);
  const [fields, setFields] = useState([]);
  const [rules, setRules] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getConfigPages().then((r) => setPages(r?.data?.data || [])).catch(() => {});
    adminAPI.getConfigFlags().then((r) => {
      const f = r?.data?.flags || {};
      setEnabled(f.ENABLE_CONFIG_RULES !== false);
    }).catch(() => {});
  }, []);

  const loadPage = async (key) => {
    setPageKey(key);
    setFormId(null);
    setFields([]);
    setRules([]);
    if (!key) return;
    try {
      const res = await adminAPI.getConfigPage(key);
      const page = res?.data?.data || res?.data;
      const list = page?.forms || [];
      setForms(list);
      if (list[0]) setFormId(list[0].id);
    } catch (_e) {
      notifyError('Unable to load forms');
    }
  };

  useEffect(() => { loadPage('qaqc'); }, []);

  const reloadRules = useCallback(async (id) => {
    if (!id) { setRules([]); setFields([]); return; }
    const [formRes, rulesRes] = await Promise.all([
      adminAPI.getConfigForm(id),
      adminAPI.getConfigValidationRules({ formId: id }),
    ]);
    const form = formRes?.data?.data || formRes?.data;
    setFields((form?.fields || []).filter((f) => f.status !== 'ARCHIVED'));
    setRules(rulesRes?.data?.data || []);
    setEnabled(rulesRes?.data?.enabled !== false);
  }, []);

  useEffect(() => {
    reloadRules(formId).catch(() => notifyError('Unable to load rules'));
  }, [formId, reloadRules]);

  const createRule = async () => {
    if (!formId) return;
    if (!draft.clauseField || !draft.actionField || !draft.actionType) {
      notifyError('Condition field, action type, and action field are required.');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.createConfigRule(formId, {
        name: draft.name || `${draft.clauseField} → ${draft.actionType} ${draft.actionField}`,
        ruleKey: draft.ruleKey || undefined,
        fieldKey: draft.fieldKey || draft.actionField,
        status: draft.status,
        conditionJson: {
          logic: draft.logic,
          clauses: [{ fieldKey: draft.clauseField, op: draft.clauseOp, value: draft.clauseValue }],
        },
        actionJson: {
          actions: [{
            type: draft.actionType,
            fieldKey: draft.actionField,
            value: draft.actionValue || undefined,
            values: draft.actionType === 'filter_options' && draft.actionValue
              ? draft.actionValue.split(',').map((s) => s.trim()).filter(Boolean)
              : undefined,
          }],
        },
      });
      notifySuccess('Rule created. Activate/publish form config as needed for runtime.');
      setDraft(emptyDraft());
      await reloadRules(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to create rule');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (rule, status) => {
    try {
      await adminAPI.updateConfigRule(rule.id, { status });
      notifySuccess(`Rule ${status}`);
      await reloadRules(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Update failed');
    }
  };

  const archiveRule = async (rule) => {
    if (!(await confirmDialog(`Archive rule "${rule.name || rule.ruleKey}"?`, 'Archive rule'))) return;
    try {
      await adminAPI.deleteConfigRule(rule.id);
      notifySuccess('Rule archived');
      await reloadRules(formId);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Archive failed');
    }
  };

  const fieldOptions = fields.map((f) => (
    <option key={f.id} value={f.fieldKey}>{f.label} ({f.fieldKey})</option>
  ));

  return (
    <div>
      <p className="ce-muted">
        Phase 5: no-code IF/THEN rules (show/hide/require/…). Circular dependencies are blocked on activate.
        Runtime applies ACTIVE rules on published QA/QC form when ENABLE_CONFIG_RULES is on.
      </p>
      <div className="filter-bar">
        <select value={pageKey} onChange={(e) => loadPage(e.target.value)}>
          <option value="">Select page</option>
          {pages.map((p) => <option key={p.pageKey} value={p.pageKey}>{p.displayName}</option>)}
        </select>
        <select value={formId || ''} onChange={(e) => setFormId(e.target.value ? Number(e.target.value) : null)} disabled={!forms.length}>
          <option value="">Select form</option>
          {forms.map((f) => <option key={f.id} value={f.id}>{f.displayName}</option>)}
        </select>
      </div>

      {formId && enabled && (
        <div className="ce-detail" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>New rule</h3>
          <div className="form-row">
            <div className="form-group"><label>Name</label><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
            <div className="form-group"><label>Status</label>
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                <option value="ACTIVE">ACTIVE</option>
                <option value="DRAFT">DRAFT</option>
              </select>
            </div>
          </div>
          <p style={{ fontWeight: 600, fontSize: 13 }}>IF</p>
          <div className="form-row">
            <div className="form-group"><label>Field</label><select value={draft.clauseField} onChange={(e) => setDraft({ ...draft, clauseField: e.target.value })}><option value="">Select</option>{fieldOptions}</select></div>
            <div className="form-group"><label>Op</label><select value={draft.clauseOp} onChange={(e) => setDraft({ ...draft, clauseOp: e.target.value })}>{OPS.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
            <div className="form-group"><label>Value</label><input value={draft.clauseValue} onChange={(e) => setDraft({ ...draft, clauseValue: e.target.value })} /></div>
          </div>
          <p style={{ fontWeight: 600, fontSize: 13 }}>THEN</p>
          <div className="form-row">
            <div className="form-group"><label>Action</label><select value={draft.actionType} onChange={(e) => setDraft({ ...draft, actionType: e.target.value })}>{ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
            <div className="form-group"><label>Target field</label><select value={draft.actionField} onChange={(e) => setDraft({ ...draft, actionField: e.target.value })}><option value="">Select</option>{fieldOptions}</select></div>
            <div className="form-group"><label>Action value</label><input value={draft.actionValue} placeholder="for set_default / filter_options" onChange={(e) => setDraft({ ...draft, actionValue: e.target.value })} /></div>
          </div>
          <button type="button" className="btn-primary btn-sm" disabled={saving} onClick={createRule}>{saving ? 'Saving…' : 'Create rule'}</button>
        </div>
      )}

      {!enabled && <p className="ce-muted">ENABLE_CONFIG_RULES is off.</p>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr><th>Rule</th><th>Condition</th><th>Actions</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>No rules defined</td></tr>
            ) : rules.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.name || r.ruleKey}</strong><div className="ce-muted"><code>{r.ruleKey}</code></div></td>
                <td style={{ fontSize: 12 }}>
                  {(r.conditionJson?.clauses || []).map((c, i) => (
                    <div key={i}>{c.fieldKey} {c.op} {Array.isArray(c.value) ? c.value.join(',') : (c.value ?? '')}</div>
                  ))}
                </td>
                <td style={{ fontSize: 12 }}>
                  {(r.actionJson?.actions || []).map((a, i) => (
                    <div key={i}>{a.type} → {a.fieldKey}</div>
                  ))}
                </td>
                <td>{r.status}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.status !== 'ACTIVE' && <button type="button" className="btn-secondary btn-sm" onClick={() => setStatus(r, 'ACTIVE')}>Activate</button>}
                  {r.status === 'ACTIVE' && <button type="button" className="btn-secondary btn-sm" onClick={() => setStatus(r, 'DRAFT')}>Deactivate</button>}
                  <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c', marginLeft: 6 }} onClick={() => archiveRule(r)}>Archive</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConfigValidationRules;
